import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import facilities from "../../constants/facilities_detail.json";
import { FacilityDetailContainer } from "containers/FacilitiesContainer";
import { FUNERAL_SIDO, FUNERAL_SIGUNGU } from "goi_common";
import RichSearch from "@/components/common/RichSearch";
import { richSearchData } from "@/constants/richSearch";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import {
  getNearbyPlaces,
  NearbyPlaces,
} from "@/containers/FacilitiesContainer/utils/getNearbyPlaces";
import { excludeFacilities } from "@/constants/facility/excludeFacilities";
import { getFacilityDisplayName, getFacilityRegionKeyword } from "@/utils/facilityTypeLabel";

// 기존 import 경로 유지용 re-export. 목록 본체는 constants/facility/excludeFacilities.ts
export { excludeFacilities };

type FacilitiesElement = typeof facilities[0];

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = facilities.map((facility) => ({
    params: {
      id: facility.id.toString(),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string | undefined;

  const findedFacilities: FacilitiesElement | undefined = id
    ? facilities.find((facility) => facility.id === parseInt(id))
    : undefined;
  let district;
  let name = "";
  let description = "";
  let hallrentString = "";
  // 지역과 붙여 쓰는 유형 표기 ("파주 수목장"의 "수목장")
  let regionKeyword = "장지";
  let breadcrumbSchema: object | null = null;
  let localBusinessSchema: object | null = null;
  let nearbyPlaces: NearbyPlaces = { facilityGroups: [], parlors: [], crematoriums: [] };

  if (findedFacilities) {
    const originSigungu =
      FUNERAL_SIGUNGU.get(Number(findedFacilities.sido))?.get(Number(findedFacilities.sigungu)) ||
      "";
    const sigungu = originSigungu?.length > 2 ? originSigungu?.slice(0, -1) : originSigungu;
    const sidoKR = FUNERAL_SIDO.get(Number(findedFacilities.sido)) || "";

    // 유형에 맞는 표기를 붙인다 (묘지는 묘지, 자연장은 자연장). 기존엔 전부 "납골당"이었다.
    name = getFacilityDisplayName(findedFacilities.companyname, findedFacilities.type);
    district = sigungu;
    regionKeyword = getFacilityRegionKeyword(findedFacilities.type);

    // 업체 요청으로 가격 비공개인 시설은 본문뿐 아니라 meta description·JSON-LD에서도 가격을 빼야 한다.
    // (본문만 가리면 검색결과 스니펫과 Schema로 가격이 그대로 새어나간다)
    const isPriceHidden = excludeFacilities.includes(String(findedFacilities.id));

    hallrentString = isPriceHidden
      ? ""
      : findedFacilities.hallrents
          .map((res) => `${res.item}: ${res.charge?.toLocaleString()}원`)
          .join(", ");

    description = isPriceHidden
      ? ` ${name} 정보, 시설 안내, ${findedFacilities.address}`
      : ` ${name} 정보, 비용, 할인 안내, ${findedFacilities.address}, ${name} 비용 ${hallrentString}`;

    // BreadcrumbList (JSON-LD, 데이터 무관): 홈 → 장지 찾기 → 현재 시설
    const SITE = "https://www.goifuneral.co.kr";
    const detailUrl = `${SITE}/facilities/${findedFacilities.id}/`;
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "고이장례연구소", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "장지 찾기", item: `${SITE}/facilities/search/` },
        { "@type": "ListItem", position: 3, name: findedFacilities.companyname, item: detailUrl },
      ],
    };

    // LocalBusiness (JSON-LD): 주소 + 좌표(geo, 100%) + 조건부 image/priceRange
    const charges = isPriceHidden
      ? []
      : (findedFacilities.hallrents ?? [])
          .map((res) => res.charge)
          .filter((c): c is number => typeof c === "number" && c > 0);
    const priceRange = charges.length
      ? `${Math.min(...charges).toLocaleString()}원 ~ ${Math.max(...charges).toLocaleString()}원`
      : null;

    localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: findedFacilities.companyname,
      description: `${findedFacilities.companyname} 위치, 비용, 시설 정보를 고이장례연구소에서 확인하세요.`,
      url: detailUrl,
      address: {
        "@type": "PostalAddress",
        streetAddress: findedFacilities.address,
        addressRegion: sidoKR || null,
        addressLocality: originSigungu || null,
        addressCountry: "KR",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: findedFacilities.latitude,
        longitude: findedFacilities.longitude,
      },
      ...(findedFacilities.images?.[0]?.url ? { image: findedFacilities.images[0].url } : {}),
      ...(priceRange ? { priceRange } : {}),
    };

    // 인근 장지·장례식장·화장장 (좌표 기반, 자기 제외) - 사진 있으면 썸네일
    nearbyPlaces = getNearbyPlaces(
      findedFacilities.longitude,
      findedFacilities.latitude,
      findedFacilities.id,
    );
  }

  return {
    props: {
      defaultFacility: findedFacilities,
      breadcrumbSchema,
      localBusinessSchema,
      nearbyPlaces,
      pageDescription: description,
      pageKeywords: `${name}, ${name} 가격, ${name} 위치, ${name} 후기, ${name} 정보, 장지검색, 봉안, 자연, 매장, 봉안당, 수목장, 화초장, 잔디장, 매장묘, 납골당, ${district} ${regionKeyword}, ${district} 장지, ${district} ${regionKeyword} 비용, ${regionKeyword} 비용`,
      pageOgTitle: `${district} ${regionKeyword}, 장지, ${name} 정보 (위치, 가격, 비용, 평점) - [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage:
        findedFacilities?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      pageCanonical: findedFacilities
        ? `https://www.goifuneral.co.kr/facilities/${findedFacilities.id}/`
        : null,
    },
  };
};

function FacilityId({
  defaultFacility,
  breadcrumbSchema,
  localBusinessSchema,
  nearbyPlaces,
}: {
  defaultFacility: FacilitiesElement;
  breadcrumbSchema: object | null;
  localBusinessSchema: object | null;
  nearbyPlaces: NearbyPlaces;
}) {
  const { FAQRichSearch } = richSearchData.facilities;

  const isExcluded = excludeFacilities.includes(String(defaultFacility.id));

  return (
    <>
      {localBusinessSchema && <RichSearch data={localBusinessSchema} />}
      {breadcrumbSchema && <RichSearch data={breadcrumbSchema} />}
      <RichSearch data={FAQRichSearch} />

      <FacilityDetailContainer
        facility={defaultFacility}
        isExcluded={isExcluded}
        nearbyPlaces={nearbyPlaces}
      />
    </>
  );
}

export default FacilityId;
